"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.configGenerator = exports.ConfigGenerator = void 0;
const tslib_1 = require("tslib");
const lib_1 = require("@feathersjs/hooks/lib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const fs_extra_1 = tslib_1.__importDefault(require("fs-extra"));
const path_1 = tslib_1.__importDefault(require("path"));
const uuid = tslib_1.__importStar(require("uuid"));
const localizeUtils_1 = require("../../../common/localizeUtils");
const telemetry_1 = require("../../../common/telemetry");
const folder_1 = require("../../../folder");
const messages_1 = require("../../messages");
const actionExecutionMW_1 = require("../../middleware/actionExecutionMW");
const settingsUtil_1 = require("../../utils/settingsUtil");
const copyPolicy_1 = require("./copyPolicy");
const jsonMerger_1 = require("./jsonMerger");
const renderTemplate_1 = require("./renderTemplate");
class ConfigGenerator {
    componentName = "ConfigGenerator";
    async run(context, destinationPath, components, features) {
        await context.userInteraction.showMessage("info", "Generating configuration files...", false);
        // telemetry props
        const successComponents = [];
        const failedComponents = [];
        // Process all components: detect conflicts and generate files in a single pass
        for (const component of components) {
            const policyKey = this.getPolicyKey(component);
            const policy = copyPolicy_1.policys[policyKey];
            if (!policy) {
                return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(this.componentName, "UnknownPolicyError", (0, localizeUtils_1.getDefaultString)("error.generator.UnknownPolicy", policyKey)));
            }
            const fileDetectionResult = await this.detectFileConflict(destinationPath, policy);
            if (fileDetectionResult.isErr()) {
                await context.userInteraction.showMessage("warn", fileDetectionResult.error.message, false);
                failedComponents.push(policyKey);
                continue;
            }
            const sourcePath = path_1.default.join((0, folder_1.getTemplatesFolder)(), "configs", component.name, component.programmingLanguage);
            await context.userInteraction.showMessage("info", `Processing ${component.name} (${component.programmingLanguage})...`, false);
            const changes = await this.generateConfigFilesByPolicy(sourcePath, destinationPath, policy, features);
            // Show what files were changed
            for (const change of changes) {
                const action = change.action === "added"
                    ? "Added"
                    : change.action === "modified"
                        ? "Modified"
                        : "Skipped";
                await context.userInteraction.showMessage("info", `  ${action}: ${change.file}`, false);
            }
            successComponents.push(policyKey);
        }
        const settingsRes = await settingsUtil_1.settingsUtil.readSettings(destinationPath, false);
        if (settingsRes.isErr())
            return (0, teamsfx_api_1.err)(settingsRes.error);
        const settings = settingsRes.value;
        if (!settings.trackingId) {
            settings.trackingId = uuid.v4();
        }
        await settingsUtil_1.settingsUtil.writeSettings(destinationPath, settings);
        context.telemetryReporter.sendTelemetryEvent(telemetry_1.TelemetryEvent.GenerateConfigSummary, {
            [telemetry_1.ProjectTypeProps.TeamsManifestCapabilities]: this.getCapabilities(features).join(",") || "",
            successComponents: successComponents.join(",") || "",
            failedComponents: failedComponents.join(",") || "",
            trackingId: settings.trackingId || "",
        });
        return (0, teamsfx_api_1.ok)({});
    }
    getCapabilities(features) {
        const capabilities = [];
        if (features["hasTab"])
            capabilities.push("Tab");
        if (features["hasBot"])
            capabilities.push("Bot");
        if (features["hasMessageExtension"])
            capabilities.push("ME");
        if (features["hasDeclarativeAgent"])
            capabilities.push("DA");
        if (features["hasCustomEngineAgent"])
            capabilities.push("CEA");
        return capabilities;
    }
    getPolicyKey(component) {
        return `${component.name}-${component.programmingLanguage}`;
    }
    async detectFileConflict(destinationPath, policy) {
        for (const [filePath, copyPolicy] of Object.entries(policy)) {
            if (!copyPolicy.allowExistingFile) {
                const fullPath = path_1.default.join(destinationPath, filePath);
                const fileExists = await fs_extra_1.default.pathExists(fullPath);
                if (fileExists) {
                    return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(this.componentName, "ConflictFileError", (0, localizeUtils_1.getDefaultString)("error.generator.FileConflictError", filePath)));
                }
            }
        }
        return (0, teamsfx_api_1.ok)(undefined);
    }
    getFileExtensionWithoutTemplate(filePath) {
        const withoutTemplate = filePath.endsWith(".tpl") ? filePath.slice(0, -4) : filePath;
        return path_1.default.extname(withoutTemplate);
    }
    async generateConfigFilesByPolicy(sourcePath, destinationPath, policy, features) {
        const changes = [];
        for (const [filePath, copyPolicy] of Object.entries(policy)) {
            const isTemplate = filePath.endsWith(".tpl");
            let srcFilePath = path_1.default.join(sourcePath, filePath);
            const destFilePath = path_1.default.join(destinationPath, isTemplate ? filePath.slice(0, -4) : filePath);
            const fileExtension = this.getFileExtensionWithoutTemplate(filePath);
            let renderedFilePath = null;
            // Render template if needed
            if (isTemplate) {
                renderedFilePath = destFilePath + ".rendered";
                const renderedContent = (0, renderTemplate_1.renderTemplate)(srcFilePath, features);
                await fs_extra_1.default.outputFile(renderedFilePath, renderedContent, "utf-8");
                srcFilePath = renderedFilePath;
            }
            try {
                // Handle existing files
                const fileExists = await fs_extra_1.default.pathExists(destFilePath);
                const relativeFilePath = path_1.default.relative(destinationPath, destFilePath);
                if (fileExists) {
                    if (copyPolicy.policy === "add" && fileExtension === ".json") {
                        await (0, jsonMerger_1.mergeJsonFile)(srcFilePath, destFilePath);
                        changes.push({ file: relativeFilePath, action: "modified" });
                    }
                    else {
                        // For "skip" or non-JSON files, do nothing
                        changes.push({ file: relativeFilePath, action: "skipped" });
                    }
                }
                else {
                    // If the file does not exist, just copy it.
                    await fs_extra_1.default.ensureDir(path_1.default.dirname(destFilePath));
                    await fs_extra_1.default.copy(srcFilePath, destFilePath);
                    changes.push({ file: relativeFilePath, action: "added" });
                }
            }
            finally {
                // Clean up rendered temp file
                if (renderedFilePath !== null) {
                    await fs_extra_1.default.remove(renderedFilePath);
                }
            }
        }
        return changes;
    }
}
exports.ConfigGenerator = ConfigGenerator;
tslib_1.__decorate([
    (0, lib_1.hooks)([
        (0, actionExecutionMW_1.ActionExecutionMW)({
            enableProgressBar: true,
            progressTitle: messages_1.ProgressTitles.create,
            progressSteps: 1,
            enableTelemetry: true,
            telemetryEventName: telemetry_1.TelemetryEvent.GenerateConfig,
        }),
    ]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, String, Array, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], ConfigGenerator.prototype, "run", null);
exports.configGenerator = new ConfigGenerator();
//# sourceMappingURL=configGenerator.js.map